import { computed, ref } from '@nuxtjs/composition-api';
import { useWebsiteCode } from '~/composables/useWebsiteCode';
import type { ApolloQueryResult } from '@apollo/client/core';
import {
  initialSearchAutocompleteQuery,
  initialBelarusSearchQuery,
  searchCustomProductsQuery,
} from '~/customQueries/search';

type InitialSearchPayload = {
  customProducts: any[] | null;
  customTitle: string | null;
  categories: any[] | null;
  sts: any[] | null;
};

type InitialSearchDataOptions = {
  customQuery: (params: { query: string; queryVariables?: Record<string, unknown> }) => Promise<any>;
  startLoading: () => number;
  finishLoading: (requestId: number) => void;
  onChange: (payload: InitialSearchPayload) => void;
};

const INITIAL_SUGGESTIONS_CACHE_TTL = 60 * 60 * 1000;

export const useInitialSearchData = ({
  customQuery,
  startLoading,
  finishLoading,
  onChange,
}: InitialSearchDataOptions) => {
  const { isBy } = useWebsiteCode();
  const INITIAL_SUGGESTIONS_CACHE_KEY = isBy.value ? 'searchInitialSuggestions:by:searchProducts' : 'searchInitialSuggestions';
  const initialCustomProducts = ref<any[] | null>(null);
  const initialCustomTitle = ref<string | null>(null);
  const initialCategories = ref<any[] | null>(null);
  const initialSts = ref<any[] | null>(null);
  const initialDataLoading = ref(false);

  const publishInitialData = () => {
    onChange({
      customProducts: initialCustomProducts.value,
      customTitle: initialCustomTitle.value,
      categories: initialCategories.value,
      sts: initialSts.value,
    });
  };

  const getCachedSuggestions = (): { categories: any[]; sts: any[] } | null => {
    if (typeof window === 'undefined') return null;

    try {
      const rawCache = window.localStorage.getItem(INITIAL_SUGGESTIONS_CACHE_KEY);
      if (!rawCache) return null;

      const cache = JSON.parse(rawCache);
      const isFresh = Number(cache?.cachedAt) + INITIAL_SUGGESTIONS_CACHE_TTL > Date.now();
      const hasSuggestions =
        (Array.isArray(cache?.categories) && cache.categories.length > 0) ||
        (Array.isArray(cache?.sts) && cache.sts.length > 0);

      if (!isFresh || !Array.isArray(cache?.categories) || !Array.isArray(cache?.sts) || !hasSuggestions) {
        window.localStorage.removeItem(INITIAL_SUGGESTIONS_CACHE_KEY);
        return null;
      }

      return {
        categories: cache.categories,
        sts: cache.sts,
      };
    } catch (error) {
      window.localStorage.removeItem(INITIAL_SUGGESTIONS_CACHE_KEY);
      return null;
    }
  };

  const cacheSuggestions = (categories: any[], sts: any[]) => {
    if (typeof window === 'undefined') return;

    try {
      if (!categories.length && !sts.length) {
        window.localStorage.removeItem(INITIAL_SUGGESTIONS_CACHE_KEY);
        return;
      }

      window.localStorage.setItem(INITIAL_SUGGESTIONS_CACHE_KEY, JSON.stringify({
        cachedAt: Date.now(),
        categories,
        sts,
      }));
    } catch (error) {
      console.error('Failed to cache initial search suggestions', error);
    }
  };

  const hasInitialPayload = computed(() => Boolean(
    initialCustomProducts.value?.length ||
    initialCategories.value?.length ||
    initialSts.value?.length
  ));

  const fetchInitialData = async () => {
    if (initialDataLoading.value) return;

    if (
      initialCustomProducts.value &&
      initialCustomTitle.value !== null &&
      initialCategories.value &&
      initialSts.value &&
      (
        initialCustomProducts.value.length > 0 ||
        initialCategories.value.length > 0 ||
        initialSts.value.length > 0
      )
    ) {
      publishInitialData();
      return;
    }

    initialDataLoading.value = true;
    const requestId = startLoading();

    try {
      const cachedSuggestions = getCachedSuggestions();
      const autocompleteRequest = cachedSuggestions
        ? Promise.resolve({ data: { AutoComplete: cachedSuggestions } })
        : customQuery({
          query: isBy.value ? initialBelarusSearchQuery : initialSearchAutocompleteQuery,
          queryVariables: { search: '' },
        }).then((response) => {
          if (!isBy.value) return response;
          const payload = response.data?.SearchProducts;
          if (response.errors?.length || !payload || payload.success === false) {
            throw new Error('Initial search suggestions are unavailable');
          }
          return { data: { AutoComplete: payload } };
        });

      const [customProductsResponse, autoCompleteResponse] = await Promise.all([
        customQuery({
          query: searchCustomProductsQuery,
          queryVariables: {
            pageSize: 4,
            currentPage: 1,
          },
        }) as Promise<ApolloQueryResult<{
          getSearchProducts?: {
            custom_products?: any[];
            custom_title?: string | null;
          };
        }>>,
        autocompleteRequest as Promise<ApolloQueryResult<{ AutoComplete?: { categories?: any[]; sts?: any[] } }>>,
      ]);

      const customProducts = customProductsResponse.data?.getSearchProducts?.custom_products ?? [];
      const customTitle = customProductsResponse.data?.getSearchProducts?.custom_title ?? '';
      const categories = autoCompleteResponse.data?.AutoComplete?.categories ?? [];
      const sts = autoCompleteResponse.data?.AutoComplete?.sts ?? [];

      if (!cachedSuggestions) {
        cacheSuggestions(categories, sts);
      }

      initialCustomProducts.value = customProducts;
      initialCustomTitle.value = customTitle;
      initialCategories.value = categories;
      initialSts.value = sts;
      publishInitialData();
    } catch (error) {
      console.error('Error fetching initial search data:', error);
      initialCustomProducts.value = [];
      initialCustomTitle.value = '';
      initialCategories.value = [];
      initialSts.value = [];
      publishInitialData();
    } finally {
      initialDataLoading.value = false;
      finishLoading(requestId);
    }
  };

  return {
    fetchInitialData,
    hasInitialPayload,
    initialCategories,
    initialDataLoading,
    initialCustomProducts,
    initialCustomTitle,
    initialSts,
  };
};
